import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  For,
  mergeProps,
} from 'solid-js';
import { createStore } from 'solid-js/store';
import { FeetInchUnit, PointCloudStylingBand } from '@trimble/types';
import { PointCloudUOMInputControl } from '../point-cloud-uom-input/point-cloud-uom-input';
import {
  POINT_CLOUD_CLEARANCE_DEFAULTS,
  PointCloudClearanceControlProps,
} from './point-cloud-clearance.model';
import { applyColorInputStyle } from '@trimble/ui-components/solidjs';
import { BtnClasses, IconButton } from '@tcweb/connect-ui';
import { t } from '@trimble/i18n';

const DISABLE_BANDS_COUNT = 4;

export const PointCloudClearanceControl: Component<
  PointCloudClearanceControlProps
> = (props: PointCloudClearanceControlProps) => {
  applyColorInputStyle();

  const activeProps = mergeProps(POINT_CLOUD_CLEARANCE_DEFAULTS, props);

  const [bands, setBands] = createStore<PointCloudStylingBand[]>([]);
  const [defaultColor, setDefaultColor] = createSignal<string>('');
  const [defaultTransparent, setDefaultTransparent] =
    createSignal<boolean>(false);

  let focusedBandIndex: number | null = null;

  createEffect(() => {
    setBands(activeProps.bands);
  });

  createEffect(() => {
    setDefaultColor(activeProps.defaultColor);
  });

  createEffect(() => {
    setDefaultTransparent(activeProps.defaultTransparent);
  });

  const removeBand = (): void => {
    const index = focusedBandIndex ?? bands.length - 1;
    const newBands = [...bands];
    newBands.splice(index, 1);
    setBands(newBands);
    handleOnBandsChange();
    focusedBandIndex = null;
  };

  const addBand = (): void => {
    const index = focusedBandIndex ?? bands.length - 1;
    const start = bands[index].start;
    const newBands = [...bands];
    newBands.splice(index, 0, { start, color: defaultColor() });
    setBands(newBands);
    handleOnBandsChange();
  };

  const updateBandStart = (index: number, start: number): void => {
    if (bands[index].start === start) {
      return;
    }

    const newBands = bands.map((band, i) =>
      i === index ? { ...band, start } : { ...band }
    );
    newBands.sort((a, b) => a.start - b.start);
    setBands(newBands);
    handleOnBandsChange();
  };

  const updateBandColor = (index: number, color: string): void => {
    setBands(index, 'color', color);
    handleOnBandsChange();
  };

  const uomSymbol = createMemo((): string => {
    return props.uom.feetInches ? FeetInchUnit : props.uom.unitLabel;
  });

  function handleDefaultColorChange(color: string): void {
    setDefaultColor(color);
    props.onDefaultColorChange?.(color);
  }

  function handleDefaultTransparentChange(transparent: boolean): void {
    setDefaultTransparent(transparent);
    props.onDefaultTransparentChange?.(transparent);
  }

  function handleOnBandsChange(): void {
    props.onBandsChange?.([...bands]);
  }

  return (
    <div class="w-full flex flex-col gap-4">
      {/* Usage */}
      <div class="flex flex-1 py-3 px-3 gap-3 text-small rounded-md border border-gray-0 bg-gray-light shadow-md">
        <span class="items-start">
          <modus-wc-icon attr:name="info" />
        </span>
        <span class="flex-1 text-xs">
          {t('mobileMapping.styles.clearance.usage')}
        </span>
      </div>

      {/* Hide points beyond bands */}
      <div class="flex flex-row gap-2">
        <span class="flex flex-1" id="clearance-hide-points-beyond-bands-label">
          {t('mobileMapping.styles.clearance.hidePointsBeyondBands')}
        </span>
        <modus-wc-switch
          aria-labelledby="clearance-hide-points-beyond-bands-label"
          value={defaultTransparent()}
          on:inputChange={(e) => {
            const transparent = (e.detail.target as HTMLInputElement).checked;
            handleDefaultTransparentChange(transparent);
          }}
        />
      </div>

      {/* Points beyond bands color */}
      <div class="flex flex-row gap-2">
        <span
          class={`flex flex-1 ${defaultTransparent() ? 'opacity-30' : ''}`}
          id="clearance-points-beyond-bands-label"
        >
          {t('mobileMapping.styles.clearance.pointsBeyondBands')}
        </span>

        <input
          aria-labelledby="clearance-points-beyond-bands-label"
          data-testid="point-cloud-clearance-default-color-input"
          type="color"
          value={defaultColor()}
          disabled={defaultTransparent()}
          onInput={(e) => {
            const color = (e.target as HTMLInputElement).value;
            handleDefaultColorChange(color);
          }}
        />
      </div>

      {/* Clearance bands */}
      <div class="flex flex-1 flex-col gap-2">
        <div class="flex flex-1 items-center gap-1">
          <span class="flex-1 font-semibold">
            {t('mobileMapping.styles.clearance.clearanceBands')}
            <span>&nbsp;({uomSymbol()})</span>
          </span>

          <IconButton
            data-cy="point-cloud-clearance-remove-band"
            color={BtnClasses.TERTIARY}
            icon="remove_heavy"
            title={t('mobileMapping.styles.clearance.removeBand')}
            disabled={bands.length < DISABLE_BANDS_COUNT}
            clicked={removeBand}
          />

          <IconButton
            data-cy="point-cloud-clearance-add-band"
            color={BtnClasses.TERTIARY}
            icon="add_heavy"
            title={t('mobileMapping.styles.clearance.addBand')}
            clicked={addBand}
          />
        </div>

        {/* Bands list */}
        <div class="flex flex-1 flex-col gap-1">
          <For each={bands}>
            {(band, index) => (
              <div class="flex items-center gap-2">
                <div class="flex-1">
                  <PointCloudUOMInputControl
                    showUomLabel={false}
                    valueInMeters={band.start}
                    uom={props.uom}
                    onCommit={(value) => {
                      updateBandStart(index(), value);
                    }}
                    onFocus={() => {
                      focusedBandIndex = index();
                    }}
                  />
                </div>
                <input
                  class="w-9 h-7"
                  type="color"
                  value={band.color}
                  onInput={(e) => {
                    updateBandColor(
                      index(),
                      (e.target as HTMLInputElement).value
                    );
                  }}
                />
              </div>
            )}
          </For>
        </div>
      </div>
    </div>
  );
};
